import { FilterOptionValueEnum } from '@fullstory/redux';
import { useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { LibrarySortState, LibraryTableSortOption, libraryTableSortOptions } from '../../../types';
import { SORT_QUERY_PARAM } from '../../../constants';
import { SortDirection } from '@fullstory/components';

export const FILTER_QUERY_PARAM = 'filterBy';
export const PAGE_QUERY_PARAM = 'page';
export const SEARCH_QUERY_PARAM = 'search';

export function getArchiveQueryParams(ctxLocationSearch: string, archivedObjID: string): string {
  const qp = new URLSearchParams(ctxLocationSearch);
  qp.set(FILTER_QUERY_PARAM, FilterOptionValueEnum.archived);
  qp.set(SEARCH_QUERY_PARAM, archivedObjID);
  return qp.toString();
}
interface LibraryObjectLandingQuery {
  page: number;
  search: string;
  sort: LibrarySortState;
  filterBy: FilterOptionValueEnum;
}
interface useValidatedQueryParams {
  maxPage?: number;
}
function isNumber(query: string): boolean {
  if (query.trim() === '') {
    return false;
  }
  return !Number.isNaN(Number(query)) && Number.isFinite(Number(query));
}

function isFilter(query: any): query is FilterOptionValueEnum {
  return query in FilterOptionValueEnum;
}

function isLibraryTableColumnKey(part: string): part is LibraryTableSortOption {
  return libraryTableSortOptions.includes(part as LibraryTableSortOption);
}

function isSortDirection(direction: string): direction is SortDirection {
  return ['asc', 'desc'].includes(direction);
}

function isLibraryTableSort(query: string): { validSort: boolean; sort: LibrarySortState } {
  const [sortColumnKey, direction] = query.split(':');
  if (
    sortColumnKey &&
    direction &&
    isLibraryTableColumnKey(sortColumnKey) &&
    isSortDirection(direction)
  ) {
    return { validSort: true, sort: { sortColumnKey, direction } };
  } else {
    return { validSort: false, sort: { sortColumnKey: 'lastModified', direction: 'desc' } };
  }
}

export const useValidatedQueryParams = ({
  maxPage,
}: useValidatedQueryParams): LibraryObjectLandingQuery => {
  const location = useLocation();
  const history = useHistory();
  const { page, filterBy, search, sort, redirect, queryParams } = useMemo(() => {
    let redirect = false;
    const queryParams = new URLSearchParams(location.search);

    let page = 1;
    if (maxPage !== undefined && maxPage > 1) {
      const pageQuery = queryParams.get(PAGE_QUERY_PARAM) || '';
      if (isNumber(pageQuery)) {
        page = parseInt(pageQuery, 10);
        if (page < 1 || page > maxPage) {
          queryParams.delete(PAGE_QUERY_PARAM);
          redirect = true;
        }
      } else {
        queryParams.delete(PAGE_QUERY_PARAM);
        redirect = true;
      }
    }

    const filterQuery = queryParams.get(FILTER_QUERY_PARAM);
    const validFilter = isFilter(filterQuery);
    const filter: FilterOptionValueEnum = validFilter
      ? (filterQuery as FilterOptionValueEnum)
      : FilterOptionValueEnum.active;
    if (!validFilter) {
      queryParams.delete(FILTER_QUERY_PARAM);
      redirect = true;
    }

    const search = queryParams.get(SEARCH_QUERY_PARAM) || '';
    if (search === '') {
      queryParams.delete(SEARCH_QUERY_PARAM);
      redirect = true;
    }

    const sortQuery = queryParams.get(SORT_QUERY_PARAM) || '';
    const { validSort, sort } = isLibraryTableSort(sortQuery);
    if (!validSort) {
      queryParams.delete(SORT_QUERY_PARAM);
      redirect = true;
    }
    return { page, filterBy: filter, search, sort, redirect, queryParams: queryParams.toString() };
  }, [location.search, maxPage]);
  useEffect(() => {
    if (redirect) {
      history.replace({ pathname: location.pathname, search: queryParams });
    }
  }, [history, location.pathname, queryParams, redirect]);
  return { page, filterBy, search, sort };
};
